import { initializeApp } from 'firebase/app';
import { getToken, getMessaging, onMessage } from 'firebase/messaging';
import { isMobile } from 'react-device-detect';
import { secureStorage } from 'utils/secureStorage';

// Firebase configuration
const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
	measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// Function to check if Firebase messaging is supported
const isMessagingSupported = () => {
	try {
		// Check if we're in a browser environment
		if (typeof window === 'undefined') return false;

		// Check if we're on mobile (Firebase messaging has limited mobile support)
		if (isMobile) return false;

		// Check for required APIs
		if (!('serviceWorker' in navigator)) return false;
		if (!('Notification' in window)) return false;
		if (!('PushManager' in window)) return false;

		// Check if we're in a secure context (HTTPS required for service workers)
		if (!window.isSecureContext) return false;

		// Check if we're not in private browsing mode (some browsers disable service workers)
		// This is a basic check - some browsers may still have issues
		if (navigator.serviceWorker === undefined) return false;

		return true;
	} catch (error) {
		console.warn('Firebase messaging support check failed:', error);
		return false;
	}
};

// Initialize messaging only if supported
export const messaging = (() => {
	try {
		if (!isMessagingSupported()) {
			console.info('Firebase messaging is not supported in this browser/environment');
			return null;
		}
		return getMessaging(firebaseApp);
	} catch (error) {
		console.error('Failed to initialize Firebase messaging:', error);
		return null;
	}
})();

// Function to get or register the service worker
export const getOrRegisterServiceWorker = async () => {
	try {
		// Check if messaging is supported
		if (!isMessagingSupported()) {
			console.warn('Service worker registration skipped: messaging not supported');
			return null;
		}

		if ('serviceWorker' in navigator) {
			return navigator.serviceWorker
				.getRegistration('/firebase-push-notification-scope')
				.then((serviceWorker) => {
					if (serviceWorker) return serviceWorker;
					return navigator.serviceWorker.register('../firebase-messaging-sw.js', {
						scope: '/firebase-push-notification-scope',
					});
				})
				.catch((error) => {
					console.error('Service worker registration failed:', error);
					return null;
				});
		}

		console.warn('Service workers are not supported in this browser');
		return null;
	} catch (error) {
		console.error('Error in service worker registration:', error);
		return null;
	}
};

// Function to get Firebase token
export const getFirebaseToken = async () => {
	try {
		if (!isMessagingSupported()) {
			console.warn('Firebase token request skipped: messaging not supported');
			return null;
		}

		if (!messaging) {
			console.warn('Firebase messaging not initialized');
			return null;
		}

		const serviceWorkerRegistration = await getOrRegisterServiceWorker();
		if (!serviceWorkerRegistration) {
			console.warn('Service worker registration failed, cannot get Firebase token');
			return null;
		}

		return getToken(messaging, {
			vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
			serviceWorkerRegistration,
		});
	} catch (error) {
		console.error('Error getting Firebase token:', error);
		return null;
	}
};

// Function to handle foreground messages
export const onForegroundMessage = () => {
	try {
		if (!isMessagingSupported() || !messaging) {
			return Promise.resolve(null);
		}
		return new Promise((resolve) => onMessage(messaging, (payload) => resolve(payload)));
	} catch (error) {
		console.error('Error setting up foreground message listener:', error);
		return Promise.resolve(null);
	}
};

// Listener for Firebase messages
export const onMessageListener = () => {
	try {
		if (!isMessagingSupported() || !messaging) {
			return Promise.resolve(null);
		}
		return new Promise((resolve) => onMessage(messaging, (payload) => resolve(payload)));
	} catch (error) {
		console.error('Error setting up message listener:', error);
		return Promise.resolve(null);
	}
};

// Function to request notification permissions
export const requestPermission = async () => {
	try {
		if (!isMessagingSupported()) {
			console.warn('Notification permission request skipped: messaging not supported');
			return;
		}

		// Check if Notification API is available
		if (!('Notification' in window)) {
			console.warn('Notifications are not supported in this browser');
			return;
		}

		const permission = await Notification.requestPermission();
		if (permission === 'granted') {
			const token = await getFirebaseToken();
			if (token) {
				secureStorage.setItem('fcmToken', token);
				console.info('Firebase token stored successfully');
			} else {
				console.warn('Failed to get Firebase token despite granted permission');
			}
		} else {
			console.warn('Notification permission denied or dismissed');
		}
	} catch (error) {
		console.error('An error occurred while requesting notification permission:', error);
	}
};

export default firebaseApp;
